<template>
  <v-container id="rma_app" class="mt-3 pa-0" v-scroll="onScroll">
    <v-card color="primary">
      <v-toolbar color="primary" flat>
        <v-tooltip bottom>
          <v-toolbar-side-icon
            @click="load(searchParams)"
            slot="activator"
            icon
          >
            <v-icon>refresh</v-icon>
          </v-toolbar-side-icon>
          <span>Refresh RMA list</span>
        </v-tooltip>
        <v-toolbar-title class="hidden-xs-only">
          <h3>RMAs ({{ totalMatches }})</h3>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-layout row wrap>
          <v-flex class="text-xs-right" xs12 sm8 offset-sm4>
            <v-text-field
              v-model="search"
              prepend-inner-icon="search"
              placeholder="Search RMAs"
              color="secondary"
              class="mx-1"
              clearable
              hide-details
              single-line
              light
              solo
            ></v-text-field>
          </v-flex>
        </v-layout>
        <TableFilterMenu
          :more-filters="0 < searchStates.length || !!search"
          v-on:filters-changed="thingsHaveChanged"
        >
          <h3>Filter by RMA Status</h3>
          <v-combobox
            class="mt-2"
            :items="states"
            v-model="searchStates"
            label="Select one or more statuses"
            color="secondary"
            @change="$root.$emit('child-changed')"
            v-on:data-cleared="searchStates = []"
            multiple
            hide-selected
            small-chips
            light
            solo
          >
            <template v-slot:selection="{ item, parent, selected }">
              <v-chip :selected="selected" label small>
                <span class="pr-2">{{ item }}</span>
                <v-icon small @click="parent.selectItem(item)">close</v-icon>
              </v-chip>
            </template>
          </v-combobox>
          <h3>Filter by Refund Type</h3>
          <v-combobox
            class="mt-2"
            :items="types"
            v-model="searchTypes"
            label="Select one or more refund type"
            color="secondary"
            @change="$root.$emit('child-changed')"
            v-on:data-cleared="searchTypes = []"
            multiple
            hide-selected
            small-chips
            light
            solo
          >
            <template v-slot:selection="{ item, parent, selected }">
              <v-chip :selected="selected" label small>
                <span class="pr-2">{{ item }}</span>
                <v-icon small @click="parent.selectItem(item)">close</v-icon>
              </v-chip>
            </template>
          </v-combobox>
        </TableFilterMenu>
      </v-toolbar>
      <v-card-text class="pa-0">
        <v-data-table
          class="primary-table mb-5"
          :headers="headers"
          :items="rmas"
          :loading="isLoading"
          :pagination.sync="pagination"
          :total-items="totalMatches"
        >
          <template v-slot:items="props">
            <tr @click="$router.push(`/rma/${props.item._id}`)">
              <td>{{ props.item.name }}</td>
              <td class="text-xs-left">
                {{ props.item.saleOrderName }}
              </td>
              <td class="text-xs-left">
                {{ props.item.customer.name }}
              </td>
              <td class="text-xs-left">
                {{ props.item.salesChannel.name }}
              </td>
              <td class="text-xs-left">
                {{ localizeDate(props.item.saleOrderDate) }}
              </td>
              <td class="text-xs-left">
                {{ localizeDate(props.item.createdOn) }}
              </td>
              <td class="text-xs-left">
                {{
                  props.item.completedOn
                    ? localizeDate(props.item.completedOn)
                    : ""
                }}
              </td>
              <td class="text-xs-left">
                {{ props.item.state }}
              </td>
              <td class="text-xs-center">
                {{ props.item.products.length }}
              </td>
              <td class="justify-center">
                <v-tooltip bottom v-if="props.item.returnVideo">
                  <v-btn slot="activator" icon>
                    <v-icon>videocam</v-icon>
                  </v-btn>
                  <span>Has Recording?</span>
                </v-tooltip>
              </td>
              <td class="justify-center layout px-0">
                <v-tooltip bottom>
                  <v-btn
                    slot="activator"
                    v-if="$route.name === 'rmas'"
                    :to="`/rma/${props.item._id}`"
                    icon
                  >
                    <v-icon>open_in_browser</v-icon>
                  </v-btn>
                  <span>Launch RMA details</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog
      transition="dialog-bottom-transition"
      v-model="dialog"
      fullscreen
      hide-overlay
      persistent
    >
      <template v-slot:activator="{ on }">
        <v-tooltip top>
          <v-btn
            slot="activator"
            key="add"
            id="addRma"
            color="secondary"
            v-on="on"
            fab
            dark
            large
          >
            <v-icon dark>add</v-icon>
          </v-btn>
          <span>Create a new RMA</span>
        </v-tooltip>
      </template>
      <v-card color="primary">
        <v-toolbar dark color="primary" flat>
          <v-toolbar-title>Create a New RMA</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <CreateRMA v-on:rma-created="completeCreate"></CreateRMA>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import CreateRMA from "@/components/rma/createRMA";
import { mapGetters, mapActions } from "vuex";
import TableFilterMenu from "@/components/app/TableFilterMenu";
import LoginCheck from "@/mixins/LoginCheck";
import Common from "@/mixins/Common";

export default {
  mixins: [LoginCheck, Common],
  data() {
    return {
      searchStates: [],
      searchTypes: [],
      dialog: false,
      dateFrom: null,
      dateTo: null,
      menu: false,
      offsetTop: 0,

      pagination: {
        page: 1,
        rowsPerPage: 10
      },

      headers: [
        {
          text: "RMA Number",
          value: "name"
        },
        {
          text: "Sale Order Name",
          value: "saleOrderName",
          align: "left"
        },
        {
          text: "Customer",
          value: "customer.name",
          align: "left"
        },
        {
          text: "Sales Channel",
          value: "salesChannel.name",
          align: "left"
        },
        {
          text: "Sale Order Date",
          value: "saleOrderDate",
          align: "left"
        },
        {
          text: "RMA Created On",
          value: "createdOn",
          align: "left"
        },
        {
          text: "Date Finalized",
          value: "completedOn",
          align: "left"
        },
        {
          text: "State",
          value: "state",
          align: "left"
        },
        {
          text: "Products RMA'd",
          value: "products.length",
          align: "left",
          sortable: false
        },
        { text: "Has Recording?", value: "returnVideo" },
        { text: "Actions", value: "name", sortable: false }
      ],
      waitForTyping: null
    };
  },

  components: {
    CreateRMA,
    TableFilterMenu
  },

  mounted() {
    this.load(this.searchParams);
  },

  computed: {
    ...mapGetters({
      rmas: "rma/getRmas",
      totalMatches: "rma/getTotalMatches",
      isLoading: "app/isLoading",
      states: "rma/getStates",
      types: "rma/getTypes",
      searchStore: "rma/getSearch"
    }),

    searchParams() {
      return {
        ...this.pagination,
        search: this.search || null,
        from: this.dateFrom || null,
        to: this.dateTo || null,
        states: this.searchStates,
        types: this.searchTypes
      };
    },
    search: {
      get() {
        return this.searchStore;
      },
      set(search) {
        this.$store.commit("rma/setSearch", search);
      }
    }
  },

  methods: {
    ...mapActions({
      load: "rma/load"
    }),

    thingsHaveChanged(e) {
      this.dateFrom = e.from;
      this.dateTo = e.to;

      if (e.cleared) {
        this.searchStates = [];
        this.searchTypes = [];
        this.$store.commit("rma/setSearch", null);
      }
      if (!this.isLoading) this.load(this.searchParams);
    },

    completeCreate(rma) {
      this.dialog = false;
      this.$router.push(`/rma/${rma._id}`);
    },

    onScroll() {
      this.offsetTop = window.pageYOffset || document.documentElement.scrollTop;
    },

    filterCalendar(from, to) {
      this.dateTo = to;
      this.dateFrom = from;

      this.load(this.searchParams);
    }
  },

  watch: {
    search(str) {
      if (str === "") {
        this.search = null;
      }
      clearTimeout(this.waitForTyping);
      this.waitForTyping = setTimeout(async () => {
        await this.load(this.searchParams);
      }, 250);
    },

    pagination: {
      handler(val, preval) {
        if (!this.dialog) this.load(this.searchParams);
      },
      deep: true
    }
  }
};
</script>

<style scoped>
>>> .v-table.theme--dark tr:hover {
  cursor: pointer;
}

#addRma {
  position: fixed;
  right: 1em;
  bottom: 1em;
}
</style>
